import React from 'react'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { sdk } from '@/lib/sdk'
import { getApiOrigin } from '@/lib/sdk-official/loader'
import { DROP_DESCRIPTION_EVENT } from '@/lib/sdk-official/resources/ai'
import { setRealtimeConnected } from './status'
import { createPresenceStore, parsePresenceFrame, type PresenceFrame, type PresenceStore } from './presenceStore'
import { createEditLockStore, type EditLockStore } from './editLocks'

/**
 * F-110: the platform socket, one per tab, community and session (it used to
 * live inside the chat). Any role gets a ticket, with the chat on or off; the
 * server only tells "something changed" (`invalidate`, ids and a topic) and the
 * screen refetches its own GET. While the socket is up the covered screens do
 * not poll; while it is down they fall back to a slow poll (`useRealtime`).
 */

/** Window event the socket relays `notification.*` on (`{ type, data }`): the header bell. */
export const NOTIFICATION_EVENT = 'memberfy:notification'

/** Rooms every socket is already in; never sent in `subscribe`. */
const AUTO_ROOMS = new Set(['me', 'community', 'staff'])
const SOCKET_PROTOCOL = 'memberfy-realtime'
/* SEC-196: the ticket goes as the second subprotocol, never in the URL. */
const socketUrl = () => `${getApiOrigin().replace(/^http/, 'ws')}/api/realtime/socket`

/** The first try (page load, or right after a working socket closed) waits 0 to 10 s, so a restart does not bring every tab back at once. */
const FIRST_DELAY_MAX_MS = 10_000
const MAX_BACKOFF_MS = 30_000
/** No real time on the server (503) or the server is full (4029): try again later, the routes carry the screen meanwhile. */
const UNAVAILABLE_RETRY_MS = 5 * 60_000
const RETRY_JITTER_MS = 30_000
/** Close codes after which reconnecting would only loop: session ended, replaced by a newer socket of the person. */
const NO_RECONNECT = new Set([4004, 4009])
const DEFAULT_PING_MS = 25_000

/** Fallback poll while the socket is down: 2 min with the tab visible, 5 min hidden, plus up to 30 s of jitter. */
export const FALLBACK_VISIBLE_MS = 2 * 60_000
export const FALLBACK_HIDDEN_MS = 5 * 60_000
export const FALLBACK_JITTER_MS = 30_000
/** Room changes within this window go out in one frame each way. */
const ROOM_BATCH_MS = 50
/** Identical notices within this window refetch once. */
const COALESCE_MS = 1_000

export interface RealtimeEvent {
  type?: string
  data?: Record<string, unknown>
  room?: string
  topic?: string
  ids?: string[]
  rooms?: string[]
  at?: string
}

export interface InvalidateFrame {
  room: string
  topic: string
  ids: string[]
  at?: string
}

/** Why a `useRealtime` callback runs: notices arrived, the socket came back (missed notices), or the fallback poll. */
export type RealtimeReason = 'invalidate' | 'reconnect' | 'fallback'

/** Synthetic events to the listeners: the socket is up again after having been up before; the fallback tick while it is down. */
const RESYNC = 'realtime.resync'
const FALLBACK = 'realtime.fallback'

interface RealtimeContextValue {
  connected: boolean
  /** From `ready` and `chat.settings`; null before the first `ready`. */
  chatEnabled: boolean | null
  listen: (listener: (event: RealtimeEvent) => void) => () => void
  /** Sends a frame when the socket is open; false otherwise. */
  send: (frame: Record<string, unknown>) => boolean
  /** Joins rooms (counted per caller) and returns the leave function. */
  join: (rooms: string[]) => () => void
  /** Area presence: holds a place in a room (`editing` inside editors) and returns the release. */
  holdPresence: (room: string, editing: boolean) => () => void
  presence: PresenceStore | null
  /** F-174 edit locks of the rooms this tab edits (`useBlockLock`). */
  locks: EditLockStore | null
}

const noop = () => {}
const RealtimeContext = React.createContext<RealtimeContextValue>({
  connected: false,
  chatEnabled: null,
  listen: () => noop,
  send: () => false,
  join: () => noop,
  holdPresence: () => noop,
  presence: null,
  locks: null
})

export const useRealtimeContext = (): RealtimeContextValue => React.useContext(RealtimeContext)

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number }; status?: number })?.response?.status ?? (err as { status?: number })?.status

const randomBetween = (min: number, max: number) => min + Math.round(Math.random() * (max - min))

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden'

export const RealtimeProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isRoleResolved } = useAuth()
  const { community } = useCommunity()
  const communityId = community?.id || null
  const myId = user?.id || null
  const sessionReady = Boolean(isAuthenticated && isRoleResolved && communityId && myId)

  const [connected, setConnected] = React.useState(false)
  const [chatEnabled, setChatEnabled] = React.useState<boolean | null>(null)
  const listenersRef = React.useRef(new Set<(event: RealtimeEvent) => void>())
  const roomCountsRef = React.useRef(new Map<string, number>())
  const socketRef = React.useRef<WebSocket | null>(null)
  /* Rooms are sent only after `ready`; before it they wait in the counts. */
  const readyRef = React.useRef(false)
  const sendRef = React.useRef<(frame: Record<string, unknown>) => boolean>(() => false)
  const [presence] = React.useState<PresenceStore>(() => createPresenceStore((frame) => sendRef.current(frame)))
  const [locks] = React.useState<EditLockStore>(() => createEditLockStore((frame) => sendRef.current(frame)))

  const emit = React.useCallback((event: RealtimeEvent) => {
    for (const listener of [...listenersRef.current]) {
      try {
        listener(event)
      } catch (error) {
        console.error('[realtime] listener failed', error)
      }
    }
  }, [])

  const send = React.useCallback((frame: Record<string, unknown>) => {
    const ws = socketRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    ws.send(JSON.stringify(frame))
    return true
  }, [])
  sendRef.current = send

  /* Room changes of one render (a screen mounting several hooks) go out as at most one
     `unsubscribe` and one `subscribe` frame: the server closes a socket above 5 frames/s. */
  const pendingRoomsRef = React.useRef<{ subscribe: Set<string>; unsubscribe: Set<string>; timer: number | null }>({ subscribe: new Set(), unsubscribe: new Set(), timer: null })
  const sendRooms = React.useCallback((type: 'subscribe' | 'unsubscribe', rooms: string[]) => {
    if (!rooms.length) return
    const pending = pendingRoomsRef.current
    const other = type === 'subscribe' ? pending.unsubscribe : pending.subscribe
    for (const room of rooms) {
      /* Left and joined again in the same batch: nothing to tell the server. */
      if (other.has(room)) other.delete(room)
      else pending[type].add(room)
    }
    if (pending.timer !== null) return
    pending.timer = window.setTimeout(() => {
      pending.timer = null
      const leave = [...pending.unsubscribe]
      const enter = [...pending.subscribe]
      pending.unsubscribe.clear()
      pending.subscribe.clear()
      /* Before `ready` the counts are the truth: `ready` subscribes them all. */
      if (!readyRef.current) return
      if (leave.length) send({ type: 'unsubscribe', rooms: leave })
      if (enter.length) send({ type: 'subscribe', rooms: enter })
    }, ROOM_BATCH_MS)
  }, [send])

  const join = React.useCallback((rooms: string[]) => {
    const wanted = [...new Set(rooms.filter((room) => room && !AUTO_ROOMS.has(room)))]
    const added: string[] = []
    for (const room of wanted) {
      const count = roomCountsRef.current.get(room) || 0
      roomCountsRef.current.set(room, count + 1)
      if (count === 0) added.push(room)
    }
    sendRooms('subscribe', added)
    return () => {
      const removed: string[] = []
      for (const room of wanted) {
        const count = (roomCountsRef.current.get(room) || 0) - 1
        if (count <= 0) {
          roomCountsRef.current.delete(room)
          removed.push(room)
        } else {
          roomCountsRef.current.set(room, count)
        }
      }
      sendRooms('unsubscribe', removed)
    }
  }, [sendRooms])

  /* A presence place keeps its room subscribed too, so a screen's `useRealtime` leaving it does not drop the place. */
  const holdPresence = React.useCallback((room: string, editing: boolean) => {
    const leaveRoom = join([room])
    const release = presence.hold(room, editing)
    return () => {
      release()
      leaveRoom()
    }
  }, [join, presence])

  /* F-186: the server sends push only while no socket of the person is visible, so the tab reports hiding and coming back. */
  React.useEffect(() => {
    const onVisibility = () => { sendRef.current({ type: 'presence.idle', idle: isHidden() }) }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  /* Input or focus marks the person active (throttled in the store). */
  React.useEffect(() => {
    const onActivity = () => presence.activity()
    const onVisible = () => { if (!isHidden()) presence.activity() }
    const events = ['keydown', 'pointerdown', 'pointermove', 'wheel', 'touchstart', 'focus'] as const
    for (const name of events) window.addEventListener(name, onActivity, { passive: true, capture: true })
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      for (const name of events) window.removeEventListener(name, onActivity, { capture: true })
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [presence])

  const listen = React.useCallback((listener: (event: RealtimeEvent) => void) => {
    listenersRef.current.add(listener)
    return () => { listenersRef.current.delete(listener) }
  }, [])

  React.useEffect(() => {
    if (!sessionReady) return
    let stopped = false
    let attempt = 0
    let everReady = false
    let revoked = false
    let waitingForVisible = false
    let retryTimer: number | undefined
    let pingTimer: number | undefined
    let ws: WebSocket | null = null

    const markDown = () => {
      readyRef.current = false
      presence.onDown()
      locks.onDown()
      setConnected(false)
      setRealtimeConnected(false)
    }

    const retry = (fixedDelay?: number) => {
      if (stopped) return
      window.clearTimeout(retryTimer)
      /* A hidden tab with the socket down does not knock: the fallback poll carries it until it is seen again. */
      if (isHidden()) {
        waitingForVisible = true
        return
      }
      const delay = fixedDelay ?? (attempt === 0
        ? randomBetween(0, FIRST_DELAY_MAX_MS)
        : randomBetween(1000, Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(attempt, 5))))
      attempt += 1
      retryTimer = window.setTimeout(connect, delay)
    }

    const onFrame = (event: RealtimeEvent) => {
      switch (event.type) {
        case 'ready': {
          readyRef.current = true
          attempt = 0
          if (typeof event.data?.chatEnabled === 'boolean') setChatEnabled(event.data.chatEnabled)
          const rooms = [...roomCountsRef.current.keys()]
          if (rooms.length) send({ type: 'subscribe', rooms })
          locks.onReady()
          presence.onReady()
          break
        }
        case 'presence': {
          const frame = parsePresenceFrame(event as unknown as Record<string, unknown>)
          if (frame) presence.onFrame(frame)
          break
        }
        case 'presence.denied':
          if (typeof event.room === 'string') presence.onDenied(event.room)
          locks.onFrame(event as unknown as Record<string, unknown>)
          break
        /* F-174: the editing join a lock waits for, the room's locks and the answers to this socket's lock frames. */
        case 'presence.joined':
        case 'presence.left':
        case 'locks':
        case 'lock.acquired':
        case 'lock.denied':
        case 'lock.released':
        case 'lock.lost':
          locks.onFrame(event as unknown as Record<string, unknown>)
          break
        case 'chat.settings':
          if (typeof event.data?.enabled === 'boolean') setChatEnabled(event.data.enabled)
          break
        case 'session.revoked':
          revoked = true
          ws?.close(1000)
          break
        case 'notification.new':
        case 'notification.read':
        case 'notification.removed':
          window.dispatchEvent(new CustomEvent(NOTIFICATION_EVENT, { detail: { type: event.type, data: event.data || {} } }))
          break
        /* F-107: a Drop's AI text is ready; the field waiting for that job listens on the window. */
        case 'ai.dropDescription.ready':
          window.dispatchEvent(new CustomEvent(DROP_DESCRIPTION_EVENT, { detail: event.data || {} }))
          break
        default:
          break
      }
      emit(event)
      if (event.type === 'ready') {
        if (everReady) emit({ type: RESYNC })
        everReady = true
      }
    }

    const connect = async () => {
      if (stopped) return
      if (isHidden()) {
        waitingForVisible = true
        return
      }
      let ticket
      try {
        ticket = await sdk.realtime.ticket()
      } catch (err) {
        const code = statusOf(err)
        /* 401 signs out on its own; 403 is not a member here: no socket, the routes carry the screens. */
        if (code === 401 || code === 403) return
        retry(code === 503 ? UNAVAILABLE_RETRY_MS + randomBetween(0, RETRY_JITTER_MS) : undefined)
        return
      }
      if (stopped || !ticket?.ticket) return
      const protocols = Array.isArray(ticket.protocols) && ticket.protocols.length === 2
        ? ticket.protocols
        : [SOCKET_PROTOCOL, ticket.ticket]
      try {
        ws = new WebSocket(socketUrl(), protocols)
      } catch {
        retry()
        return
      }
      const socket = ws
      socketRef.current = socket
      socket.onopen = () => {
        setConnected(true)
        setRealtimeConnected(true)
        /* F-186: a socket opened in a hidden tab says so at once, or the server would hold back the person's push. */
        if (isHidden()) socket.send(JSON.stringify({ type: 'presence.idle', idle: true }))
        pingTimer = window.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'ping' }))
        }, ticket.pingIntervalMs || DEFAULT_PING_MS)
      }
      socket.onmessage = (message) => {
        let parsed: RealtimeEvent | null = null
        try {
          parsed = JSON.parse(typeof message.data === 'string' ? message.data : '')
        } catch {
          return
        }
        if (parsed && typeof parsed === 'object') onFrame(parsed)
      }
      socket.onclose = (closed) => {
        window.clearInterval(pingTimer)
        if (socketRef.current === socket) socketRef.current = null
        markDown()
        if (stopped) return
        if (revoked || NO_RECONNECT.has(closed.code)) {
          /* The session is gone: the next route call answers 401 and the app signs out. */
          if (closed.code === 4004 || revoked) emit({ type: 'session.revoked' })
          return
        }
        if (closed.code === 4029) {
          retry(UNAVAILABLE_RETRY_MS + randomBetween(0, RETRY_JITTER_MS))
          return
        }
        /* attempt is 0 again after a socket that reached `ready`: the first retry waits 0 to 10 s. */
        retry()
      }
    }

    const onVisibility = () => {
      if (!waitingForVisible || isHidden()) return
      waitingForVisible = false
      retry(randomBetween(0, 3000))
    }
    document.addEventListener('visibilitychange', onVisibility)

    retry()
    return () => {
      stopped = true
      document.removeEventListener('visibilitychange', onVisibility)
      window.clearTimeout(retryTimer)
      window.clearInterval(pingTimer)
      if (ws && ws.readyState <= WebSocket.OPEN) ws.close(1000)
      socketRef.current = null
      markDown()
      setChatEnabled(null)
    }
  }, [sessionReady, communityId, myId, emit, send, presence, locks])

  /* One fallback clock for the whole tab while the socket is down, so every screen refetches on the same
     tick: 2 min visible, 5 min hidden, plus up to 30 s of jitter; re-armed when the tab is hidden or shown. */
  React.useEffect(() => {
    if (!sessionReady || connected) return
    let timer: number | undefined
    let lastAt = Date.now()
    let jitter = randomBetween(0, FALLBACK_JITTER_MS)
    const arm = () => {
      window.clearTimeout(timer)
      const interval = isHidden() ? FALLBACK_HIDDEN_MS : FALLBACK_VISIBLE_MS
      timer = window.setTimeout(() => {
        lastAt = Date.now()
        jitter = randomBetween(0, FALLBACK_JITTER_MS)
        emit({ type: FALLBACK })
        arm()
      }, Math.max(0, lastAt + interval + jitter - Date.now()))
    }
    arm()
    document.addEventListener('visibilitychange', arm)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', arm)
    }
  }, [sessionReady, connected, emit])

  const value = React.useMemo<RealtimeContextValue>(
    () => ({ connected, chatEnabled, listen, send, join, holdPresence, presence, locks }),
    [connected, chatEnabled, listen, send, join, holdPresence, presence, locks],
  )

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>
}

/** Every raw socket event (`ready`, `message.new`, `notification.*`…), for the chat and the bell. */
export const useRealtimeEvents = (handler: (event: RealtimeEvent) => void, enabled = true) => {
  const { listen } = useRealtimeContext()
  const handlerRef = React.useRef(handler)
  handlerRef.current = handler
  React.useEffect(() => {
    if (!enabled) return
    return listen((event) => handlerRef.current(event))
  }, [listen, enabled])
}

interface UseRealtimeOptions {
  /** Only these topics call back (none given: every topic of the rooms). An empty list: only reconnect and fallback. */
  topics?: string[]
  enabled?: boolean
  /** Which reasons call back (default all three). */
  reasons?: RealtimeReason[]
}

/**
 * A screen's live data: joins `rooms` (`me`, `community` and `staff` are automatic), and calls
 * `onInvalidate` once per second at most for the notices of those rooms and topics (identical ones
 * merged), once after the socket comes back, and, while the socket is down, every 2 min with the tab
 * visible or 5 min hidden (plus up to 30 s of jitter). With the socket up nothing is polled.
 */
export const useRealtime = (
  rooms: string[],
  onInvalidate: (frames: InvalidateFrame[], reason: RealtimeReason) => void,
  options: UseRealtimeOptions = {},
) => {
  const { connected, listen, join } = useRealtimeContext()
  const { enabled = true } = options
  const callbackRef = React.useRef(onInvalidate)
  callbackRef.current = onInvalidate
  const roomsKey = [...new Set(rooms.filter(Boolean))].sort().join(',')
  const topicsKey = options.topics ? options.topics.join(',') : null
  const reasonsKey = (options.reasons || ['invalidate', 'reconnect', 'fallback']).join(',')

  const fire = React.useCallback((frames: InvalidateFrame[], reason: RealtimeReason) => {
    if (!reasonsKey.split(',').includes(reason)) return
    callbackRef.current(frames, reason)
  }, [reasonsKey])

  React.useEffect(() => {
    if (!enabled || !roomsKey) return
    return join(roomsKey.split(','))
  }, [enabled, roomsKey, join])

  React.useEffect(() => {
    if (!enabled) return
    const roomSet = new Set(roomsKey ? roomsKey.split(',') : [])
    const topicSet = topicsKey === null ? null : new Set(topicsKey ? topicsKey.split(',') : [])
    let buffer = new Map<string, InvalidateFrame>()
    let timer: number | undefined
    const flush = () => {
      timer = undefined
      const frames = [...buffer.values()]
      buffer = new Map()
      if (frames.length) fire(frames, 'invalidate')
    }
    const off = listen((event) => {
      if (event.type === RESYNC) {
        fire([], 'reconnect')
        return
      }
      if (event.type === FALLBACK) {
        fire([], 'fallback')
        return
      }
      if (event.type !== 'invalidate' || typeof event.topic !== 'string' || typeof event.room !== 'string') return
      if (!roomSet.has(event.room)) return
      if (topicSet && !topicSet.has(event.topic)) return
      const ids = Array.isArray(event.ids) ? event.ids.filter((id) => typeof id === 'string') : []
      /* The same change reaches several rooms (community, space, item): one refetch. */
      const key = `${event.topic}|${[...ids].sort().join(',')}`
      if (!buffer.has(key)) buffer.set(key, { room: event.room, topic: event.topic, ids, at: event.at })
      if (timer === undefined) timer = window.setTimeout(flush, COALESCE_MS)
    })
    return () => {
      off()
      window.clearTimeout(timer)
    }
  }, [enabled, roomsKey, topicsKey, listen, fire])

  return { connected }
}

/**
 * Area presence of a room: holds this tab's place there (`editing` inside an
 * editor) while mounted and returns who else is there (null: nobody known yet,
 * the socket is down, or the room was refused). Never yourself.
 */
export const usePresence = (
  room: string | null | undefined,
  { editing = false, enabled = true, hold = true }: { editing?: boolean; enabled?: boolean; /** false: only reads a place someone else holds. */ hold?: boolean } = {},
): PresenceFrame | null => {
  const { holdPresence, presence } = useRealtimeContext()
  const active = Boolean(enabled && room && presence)
  React.useEffect(() => {
    if (!active || !room || !hold) return
    return holdPresence(room, editing)
  }, [active, room, editing, hold, holdPresence])
  const subscribe = React.useCallback((listener: () => void) => (active && room && presence ? presence.subscribe(room, listener) : noop), [active, room, presence])
  const read = React.useCallback(() => (active && room && presence ? presence.get(room) : null), [active, room, presence])
  return React.useSyncExternalStore(subscribe, read, () => null)
}

/** Who saved an item, from the socket's `item.saved` (`by` is null for a save without a session person). */
export interface ItemSavedNotice {
  room: string
  itemType: string
  itemId: string
  by: { memberId: string; name: string | null } | null
  updatedAt: string | null
  at?: string
}

/** Calls back on every `item.saved` of `room` (the team and the editors there get it). */
export const useItemSaved = (room: string | null | undefined, handler: (notice: ItemSavedNotice) => void, enabled = true) => {
  useRealtimeEvents((event) => {
    if (event.type !== 'item.saved' || !room || event.room !== room) return
    const raw = event as unknown as Record<string, unknown>
    const by = raw.by as { memberId?: unknown; name?: unknown } | null
    handler({
      room,
      itemType: typeof raw.itemType === 'string' ? raw.itemType : '',
      itemId: typeof raw.itemId === 'string' ? raw.itemId : '',
      by: by && typeof by.memberId === 'string' ? { memberId: by.memberId, name: typeof by.name === 'string' ? by.name : null } : null,
      updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : null,
      at: typeof raw.at === 'string' ? raw.at : undefined,
    })
  }, enabled && Boolean(room))
}
